import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useSeo } from "@/lib/seo";
import ArticleCard from "@/components/public/ArticleCard";
import Newsletter from "@/components/public/Newsletter";
import SectionHeader from "@/components/public/SectionHeader";
import { Reveal } from "@/components/public/Reveal";
import { BrushUnderline } from "@/components/public/Brush";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const { settings } = useSettings();
  const hero = settings.hero || {};
  const [data, setData] = useState({ featured: [], latest: [], trending: [], ideas: [], ecommerce: [], ai: [], realtalk: [], editors: [], popular: [] });

  useSeo({ title: "Lucrative Laptop | Modern business for regular people", description: settings.default_seo_description || "Real ideas. Real tools. No business degree required.", image: settings.default_social_image });

  useEffect(() => {
    const g = (params) => api.get("/public/posts", { params }).then((r) => r.data).catch(() => []);
    Promise.all([
      g({ section: "featured", limit: 1 }), g({ limit: 7 }), g({ section: "trending", limit: 4 }),
      g({ category: "ideas", limit: 3 }), g({ category: "e-commerce", limit: 3 }), g({ category: "ai-tools", limit: 2 }),
      g({ category: "real-talk", limit: 3 }), g({ section: "editors", limit: 4 }), g({ section: "popular", limit: 5 }),
    ]).then(([featured, latest, trending, ideas, ecommerce, ai, realtalk, editors, popular]) =>
      setData({ featured, latest, trending, ideas, ecommerce, ai, realtalk, editors, popular }));
  }, []);

  const featured = data.featured[0] || data.latest[0];
  const latestRest = data.latest.filter((p) => p.slug !== featured?.slug).slice(0, 6);

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* HERO */}
      <section className="relative px-5 lg:px-10 pt-8 lg:pt-10 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          <div className="lg:col-span-7 relative z-10">
            <Reveal>
              <div className="flex items-center gap-3 font-cond font-bold uppercase tracking-[0.25em] text-sm text-white/80 mb-6">
                <span className="text-brand">IDEAS</span><span className="text-ink-600">/</span>
                <span>ACTION</span><span className="text-ink-600">/</span>
                <span className="text-brand">FREEDOM</span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="poster poster-xl">
                {hero.headline_prefix || "BUILD"}{" "}
                <span className="relative inline-block text-brand">{hero.headline_highlight || "SOMETHING"}
                  <BrushUnderline className="absolute left-0 -bottom-2 lg:-bottom-4 w-full h-4 lg:h-6 text-brand" />
                </span>{" "}
                {hero.headline_suffix || "OF YOUR OWN."}
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="text-white/65 text-lg lg:text-xl mt-8 max-w-xl leading-relaxed">{hero.subtitle || "Practical ideas, tools and real talk for people creating something of their own."}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="flex flex-wrap items-center gap-4 mt-9">
                <Link to={hero.cta_link || "/category/ideas"} data-testid="hero-cta" className="btn-poster"><span>{(hero.cta_text || "SEE WHAT'S POSSIBLE").toUpperCase()}</span><ArrowRight size={17} className="relative z-10" /></Link>
                <a href="#latest" className="btn-ghost"><span>LATEST STORIES</span><ArrowDown size={16} /></a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={0.15}>
              <div className="relative group overflow-hidden border border-ink-600 h-[340px] lg:h-[520px]">
                <img src={hero.image || HERO_FALLBACK} alt="Modern entrepreneur workspace at night" loading="eager" onError={(e) => { if (e.currentTarget.src !== HERO_FALLBACK) e.currentTarget.src = HERO_FALLBACK; }} className="w-full h-full object-cover img-zoom duotone" />
                <div className="absolute inset-0 vignette" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-ink-950/20" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-hand text-brand text-2xl lg:text-3xl leading-tight rotate-[-2deg]">Your Brand. Your Move.</p>
                </div>
              </div>
            </Reveal>
            <span className="hidden lg:block absolute -top-6 -left-8 font-poster text-stroke text-[7rem] leading-none opacity-40 -z-0 select-none" aria-hidden="true">01</span>
          </div>
        </div>
      </section>

      {/* FEATURED + TRENDING */}
      {featured && (
        <section className="px-5 lg:px-10 pb-20">
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8"><Reveal><ArticleCard post={featured} variant="hero" /></Reveal></div>
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-5"><span className="w-6 h-0.5 bg-brand" /><span className="label-mono">Trending Now</span></div>
              <div className="flex flex-col gap-3">
                {(data.trending.length ? data.trending : data.latest.slice(0, 4)).map((p, i) => <Reveal key={p.slug} delay={i * 0.05}><ArticleCard post={p} variant="horizontal" /></Reveal>)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LATEST */}
      {latestRest.length > 0 && (
        <section id="latest" className="px-5 lg:px-10 pb-20 scroll-mt-24">
          <SectionHeader number="02" label="Fresh off the laptop" title="WHAT'S" titleGreen="NEW." link="/category/business" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestRest.map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.06}><ArticleCard post={p} variant="default" /></Reveal>)}
          </div>
        </section>
      )}

      {/* IDEAS bento */}
      {data.ideas.length > 0 && (
        <section className="px-5 lg:px-10 pb-20">
          <SectionHeader number="03" label="Ideas" title="WHAT IF" titleGreen="THIS WORKED?" link="/category/ideas" />
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2"><Reveal><ArticleCard post={data.ideas[0]} variant="wide" /></Reveal></div>
            <div className="flex flex-col gap-6">{data.ideas.slice(1, 3).map((p, i) => <Reveal key={p.slug} delay={i * 0.06}><ArticleCard post={p} variant="horizontal" /></Reveal>)}</div>
          </div>
        </section>
      )}

      {/* E-COMMERCE + AI split */}
      <section className="px-5 lg:px-10 pb-20 grid lg:grid-cols-2 gap-10 lg:gap-12">
        {data.ecommerce.length > 0 && (
          <div>
            <SectionHeader label="E-Commerce" title="SELL" titleGreen="SOMETHING REAL." link="/category/e-commerce" />
            <div className="flex flex-col gap-4">{data.ecommerce.map((p, i) => <Reveal key={p.slug} delay={i * 0.05}><ArticleCard post={p} variant="horizontal" /></Reveal>)}</div>
          </div>
        )}
        {data.ai.length > 0 && (
          <div>
            <SectionHeader label="AI + Tools" title="WORK" titleGreen="SMARTER." link="/category/ai-tools" />
            <div className="grid sm:grid-cols-2 gap-4">{data.ai.map((p, i) => <Reveal key={p.slug} delay={i * 0.05}><ArticleCard post={p} variant="default" /></Reveal>)}</div>
          </div>
        )}
      </section>

      {/* NEWSLETTER */}
      {settings.newsletter_enabled !== false && (
        <section className="px-5 lg:px-10 pb-20"><Reveal><Newsletter headline={settings.newsletter_headline} subtext={settings.newsletter_subtext} /></Reveal></section>
      )}

      {/* REAL TALK */}
      {data.realtalk.length > 0 && (
        <section className="px-5 lg:px-10 pb-20">
          <SectionHeader number="04" label="Real Talk" title="NO" titleGreen="GURU SPEECH." link="/category/real-talk" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.realtalk.map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.06}><ArticleCard post={p} variant="default" /></Reveal>)}</div>
        </section>
      )}

      {/* POPULAR + EDITORS */}
      <section className="px-5 lg:px-10 pb-28 grid lg:grid-cols-12 gap-10 lg:gap-12">
        {data.popular.length > 0 && (
          <div className="lg:col-span-7">
            <SectionHeader number="05" label="Popular this week" title="WORTH" titleGreen="YOUR TIME." />
            <div className="flex flex-col">
              {data.popular.map((p, i) => (
                <Link key={p.slug} to={`/article/${p.slug}`} className="group flex items-center gap-6 py-5 border-b border-ink-700 hover:border-brand/50 transition-colors">
                  <span className="font-poster text-stroke text-4xl lg:text-5xl w-14 shrink-0 group-hover:text-brand group-hover:[-webkit-text-stroke:0] transition-colors">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0">
                    <div className="font-cond uppercase tracking-[0.2em] text-brand text-xs mb-1">{(p.category || "").replace(/-/g, " ")}</div>
                    <h4 className="font-display font-bold uppercase text-white text-lg lg:text-2xl leading-tight line-clamp-2 group-hover:text-brand transition-colors">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        {data.editors.length > 0 && (
          <div className="lg:col-span-5">
            <SectionHeader label="Hand picked" title="EDITOR'S" titleGreen="PICKS." />
            <div className="flex flex-col gap-4">{data.editors.map((p, i) => <Reveal key={p.slug} delay={i * 0.05}><ArticleCard post={p} variant="horizontal" /></Reveal>)}</div>
          </div>
        )}
      </section>
    </div>
  );
}
